import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { GptCompletionMessage } from '../../common/general-types/open-ai.dto';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ChatGptService {

  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);
  
  sendCompletionMessage(messages: GptCompletionMessage[]) {
    const completion_message_url = `${environment.shout_api.url}/chat-gpt`;
    return this.http.post(completion_message_url, messages, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /**
   * Streams a chat completion token-by-token from the backend SSE endpoint.
   *
   * Yields each text delta as it arrives so callers can render the model's output live.
   * Consume with `for await (const delta of chatGptService.streamCompletionMessage(messages)) { ... }`.
   */
  async *streamCompletionMessage(messages: GptCompletionMessage[]): AsyncGenerator<string, void, unknown> {

    console.log('streamCompletionMessage', messages);

    const stream_url = `${environment.shout_api.url}/chat-gpt/stream`;

    const response = await fetch(stream_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.firebaseService.accessToken ?? ''}`,
      },
      body: JSON.stringify(messages),
    });

    if (!response.ok || !response.body) {
      throw new Error(`Stream request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line.
      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const event of events) {
        const line = event.trim();
        if (!line.startsWith('data:')) {
          continue;
        }

        const payload = line.slice('data:'.length).trim();
        if (payload === '[DONE]') {
          return;
        }

        try {
          const parsed = JSON.parse(payload) as { delta?: string; error?: string };
          if (parsed.error) {
            throw new Error(parsed.error);
          }
          if (parsed.delta) {
            yield parsed.delta;
          }
        } catch (err) {
          // Ignore malformed/partial JSON fragments; the next chunk should complete them.
          console.warn('streamCompletionMessage: skipped unparsable SSE payload', err);
        }
      }
    }
  }

}
